import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { StepUpDialog } from "../components/ui/StepUpDialog";
import { Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { WithdrawalRequest } from "../api/types";

const statusTone = { pending: "warn", approved: "success", rejected: "danger", paid: "gold" } as const;

// Only approve/reject move money and require step-up — "paid" is a
// bookkeeping confirmation after that decision already happened, matching
// the same distinction the Worker itself enforces (see routes/admin/withdrawals.js).
type PendingAction = { request: WithdrawalRequest; kind: "approve" | "reject" | "paid" };

export function WithdrawalsPage() {
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected" | "paid">("pending");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<WithdrawalRequest>("/admin/withdrawals", {
    status: statusFilter,
  });
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [showStepUp, setShowStepUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  function beginAction(request: WithdrawalRequest, kind: PendingAction["kind"]) {
    setPending({ request, kind });
    if (kind === "paid") {
      // Not step-up protected — proceed straight to the plain confirm modal.
    } else {
      setShowStepUp(true);
    }
  }

  async function runAction(stepUpToken?: string) {
    if (!pending) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/withdrawals/${pending.request.id}/${pending.kind}`, {
        method: "POST",
        body: stepUpToken ? { stepUpToken } : undefined,
      });
      showToast(`Withdrawal ${pending.kind}`, "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update withdrawal", "error");
    } finally {
      setIsSubmitting(false);
      setPending(null);
      setShowStepUp(false);
    }
  }

  const columns: Column<WithdrawalRequest>[] = [
    { header: "User ID", render: (row) => <span className="font-mono text-xs text-ink-muted">{row.user_id.slice(0, 8)}...</span> },
    { header: "Coins", render: (row) => <span className="font-semibold">{row.coins_amount.toLocaleString()}</span> },
    { header: "Cash Value", render: (row) => `$${row.cash_amount.toFixed(2)}` },
    { header: "Method", render: (row) => row.payout_method },
    { header: "Status", render: (row) => <Badge tone={statusTone[row.status]}>{row.status}</Badge> },
    { header: "Requested", render: (row) => new Date(row.created_at).toLocaleString() },
    {
      header: "",
      className: "text-right",
      render: (row) =>
        row.status === "pending" ? (
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="danger" onClick={() => beginAction(row, "reject")}>
              Reject
            </Button>
            <Button size="sm" variant="primary" onClick={() => beginAction(row, "approve")}>
              Approve
            </Button>
          </div>
        ) : row.status === "approved" ? (
          <Button size="sm" variant="secondary" onClick={() => beginAction(row, "paid")}>
            Mark Paid
          </Button>
        ) : null,
    },
  ];

  return (
    <AppShell title="Withdrawals">
      <div className="mb-4">
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className="w-auto">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="paid">Paid</option>
        </Select>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No pending withdrawals"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      {/* "paid" — a bookkeeping confirmation, not step-up protected */}
      <Modal
        isOpen={!!pending && pending.kind === "paid"}
        onClose={() => setPending(null)}
        title="Mark this withdrawal as paid?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={() => runAction()} isLoading={isSubmitting}>
              Mark Paid
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">Confirms you've sent the payout to this user outside the system.</p>
      </Modal>

      {/* approve/reject — real money decisions, step-up protected */}
      <StepUpDialog
        isOpen={showStepUp}
        onClose={() => {
          setShowStepUp(false);
          setPending(null);
        }}
        onVerified={(token) => runAction(token)}
        title={pending ? `${pending.kind.charAt(0).toUpperCase()}${pending.kind.slice(1)} withdrawal?` : ""}
        description={
          pending
            ? `Coins: ${pending.request.coins_amount.toLocaleString()} — Cash value: $${pending.request.cash_amount.toFixed(2)} — Method: ${pending.request.payout_method}. ${
                pending.kind === "reject" ? "Rejecting automatically refunds the coins to the user. " : ""
              }This is a real-money action — re-enter your password to confirm.`
            : undefined
        }
      />
    </AppShell>
  );
}
